// ─────────────────────────────────────────────────────────────────────────────
// Dashboard, CD's redesigned desktop dashboard, built 1:1 in React.
//
// This is a faithful replica of CD's mockup (docs/handoff/cd-dashboard/delivered/
// dashboard.html + dashboard-empty.html). The CSS is ported verbatim into
// ./dashboard/dashboard.css; the markup is split into section components under
// ./dashboard/ that each render CD's exact classes. Every slot is data-driven
// from the typed data layer (lib/useDashboardData → the live workers): each
// section renders CD's POPULATED markup when its data is present and CD's EMPTY
// markup when it's null/absent. Nothing is hardcoded, the Whitsett numbers in
// the mockup come from data here.
//
// Scope: the desktop OWNER view (the maximal surface). Mobile + role-gating are
// out of scope for this task; the Merch card flags where a financial role-gate
// would later apply (see ./dashboard/Merch.tsx).
// ─────────────────────────────────────────────────────────────────────────────

import { Skeleton } from '../ui/Skeleton';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useDashboardData, canSeeMoney, effectiveRoleKey, getCachedUser, firstRunSync, firstRunSyncDone } from '../lib';
import './dashboard-sections/dashboard.css';
import {
  TopNav,
  Spotlight,
  BandMemberSpotlight,
  TourManagerSpotlight,
  ProductionSpotlight,
  MerchSpotlight,
  RecentShows,
  TmNext3,
  ProdVenueSpecs,
  MerchBestsellers,
  RecentRelease,
  Opportunities,
  Merch,
  MerchQrCard,
  NeedsMediaCard,
  Upcoming,
  Todo,
  Trending,
  StreamingSection,
  AudienceRail,
  TopCities,
  DashFooter,
  useColumnAlign,
  ShopifyBillingGate,
} from './dashboard-sections';
import { ArrowRight } from './dashboard-sections/glyphs';

export function Dashboard() {
  const { data, loading, error, reload, fullLoading } = useDashboardData();

  // fetchRecentRelease builds the /release-analytics slug from the band name,
  // expose it on window the same way the legacy app does (the data layer reads
  // window.acBandName). Set as soon as the user resolves.
  useEffect(() => {
    if (data?.user?.band_name) {
      (window as { acBandName?: string }).acBandName = data.user.band_name;
    }
  }, [data?.user?.band_name]);

  /* first-run-sync 2026-07-18 (Ryan: "a new artist shouldn't sit half-empty
     for a day"), the connect-prime covers the settings-drawer connect path;
     THIS covers the OAuth-redirect path (which bypasses the drawer) and any
     account that connected before this shipped. Fires exactly once per account
     (marker-gated inside firstRunSync); org/roster seats have no single band to
     warm, so are skipped. Fire-and-forget, never blocks the dashboard. */
  useEffect(() => {
    const u = data?.user;
    if (!u) return;
    const root = String(u.account_root || '').toLowerCase();
    const isBand = root === '' || root === 'artist' || u.is_band_primary === true;
    if (!isBand || firstRunSyncDone()) return;
    void firstRunSync(false).catch(() => { /* best-effort */ });
  }, [data?.user]);

  // Re-run CD's column seam-alignment whenever the data settles (heights change).
  useColumnAlign(!loading);

  // While the first load is in flight (or on a hard error) render CD's chrome
  // with EVERY section in its empty state, honest, never filler (Bible 06.4).
  // Seed from the warm module cache so page-to-page nav resolves the role on the
  // FIRST paint, otherwise effectiveRoleKey(null) fails closed to band_member
  // and the trimmed band-member dashboard flashes before /api/me lands.
  const user = data?.user ?? getCachedUser() ?? null;
  const stats = data?.stats ?? null;
  const spotlightPhoto = data?.spotlightPhoto ?? '';
  // The Spotlight "Top mover" is the PLATFORM with the biggest 30-day audience
  // growth (2026-07-07, Ryan), computed in the data layer where the trend
  // snapshots live. (Was topMoverOf(data.movers): the top content item.)
  const topMover = data?.topMover ?? null;
  const opportunities = data?.opportunities ?? [];
  const inviteeAvatars = data?.inviteeAvatars ?? {};
  const upcomingShows = data?.upcomingShows ?? [];
  const merch = data?.merch ?? null;
  const recentRelease = data?.recentRelease ?? null;
  const topPlaylists = data?.topPlaylists ?? [];
  const todos = data?.todos ?? [];
  const topCities = data?.topCities ?? null;
  // Prefer the live music-endpoint summary (same source as the working playlists)
  // over the dashboard snapshot, which is frequently empty. See lib/fetchArtistMusic.
  const spotify = data?.spotifyLive ?? stats?.raw.spotify;
  // The band's data id (X-Band-Id for the playlist-artwork fetch), the SAME id
  // fetchTopPlaylists keys on: the band PRIMARY/founder's id (band_user_id),
  // falling back to user.id. See lib/dashboard.fetchPlaylistArtwork.
  const bandId =
    user?.band_user_id != null ? String(user.band_user_id) : user?.id != null ? String(user.id) : '';

  // A load error is surfaced quietly (the empty states already read honestly);
  // logging keeps it debuggable without breaking the page.
  if (error) {
    // eslint-disable-next-line no-console
    console.warn('[Dashboard] data load failed:', error.message);
  }

  // Role-specific dashboard shapes. The band member + tour manager both get an
  // ops-style trimmed dashboard (role hero + a secondary strip replacing Biggest
  // movers; no Recent release / Top Cities / Audience). Band member additionally
  // moves To-Dos full-width below; the TM keeps the standard rail.
  // Only branch to a role-specific layout once the user is actually resolved.
  // With no user yet (cold load, cache cold) fall through to the default
  // (streaming) layout rather than the trimmed band-member one.
  const role = user ? effectiveRoleKey(user) : '';
  const isBandMember = role === 'band_member';
  const isTM = role === 'tour_manager';
  const isProduction = role === 'foh_engineer' || role === 'monitor_engineer' || role === 'tech';
  const isMerch = role === 'merch_manager';
  const isContent = role === 'content_creator' || role === 'social_media_manager';
  const trimmed = isBandMember || isTM || isProduction || isMerch;
  // trending-gate 2026-07-06 (Ryan): on the ARTIST side, Trending is only for
  // Jedi + the artist OWNER + AV band members, NOT crew lenses (TM, production,
  // content, merch, touring band member). The roster side has its own gate.
  const canSeeTrending = role === 'jedi' || role === 'artist' || role === 'band_member_av';

  return (
    <>
      <TopNav user={user} />
      {/* shopify-billing 2026-07-09: App Pricing plan gate, renders ONLY for
          Shopify-App-Store-sourced accounts with no active subscription
          (App Review 1.2.2's required in-app plan-selection path). Stripe /
          organic accounts never see it; fails open. */}
      <ShopifyBillingGate />

      <main className="page" data-screen-label="Dashboard">
        <div className="dash-grid">
          {/* LEFT / MAIN COLUMN, a role-specific hero, then the secondary strip
              that sits in the Biggest-movers slot. */}
          <div className="col-main">
            {isBandMember ? (
              <>
                <BandMemberSpotlight photoUrl={spotlightPhoto} />
                <RecentShows />
              </>
            ) : isTM ? (
              <>
                <TourManagerSpotlight photoUrl={spotlightPhoto} />
                <TmNext3 bestsellers={merch?.bestsellers} />
                {/* To-Dos sit under the strip at the main-column (2/3) width. */}
                {/* audit 2026-07-02: pass reload so task edits refresh the list. */}
                <Todo todos={todos} onChanged={reload} />
              </>
            ) : isProduction ? (
              <>
                <ProductionSpotlight photoUrl={spotlightPhoto} />
                <ProdVenueSpecs />
              </>
            ) : isMerch ? (
              <>
                {fullLoading && !merch ? <Skeleton rows={3} pad="22px" /> : <>
                <MerchSpotlight merch={merch} photoUrl={spotlightPhoto} />
                <MerchBestsellers merch={merch} />
                </>}
              </>
            ) : (
              <>
                <Spotlight stats={stats} topMover={topMover} photoUrl={spotlightPhoto} />
                {/* Trending replaces Biggest movers in this slot (2026-07-07,
                    Ryan): the daily spike feed is more valuable than the
                    total-ranked movers grid. Same role gate as the old rail
                    card (Jedi + artist owner + AV band member); non-trending
                    seats simply skip the slot. `wide` → 2-up pill grid. */}
                {canSeeTrending && <Trending bandId={bandId} wide />}
                {/* flash-fix 2026-07-24: the fast flight has no release data —
                    "No releases yet" may only render once the FULL load settles. */}
                {fullLoading && !recentRelease ? <Skeleton rows={4} pad="22px" /> : <RecentRelease release={recentRelease} />}
              </>
            )}
          </div>

          {/* RIGHT RAIL */}
          <aside className="dash-rail">
            {/* Trending moved OUT of the rail into the main-column slot that used
                to hold Biggest movers (2026-07-07, Ryan). The rail now leads
                with Opportunities. */}
            <Opportunities
              opportunities={opportunities}
              avatars={inviteeAvatars}
              onChanged={reload}
              currentUserId={user?.id ?? null}
            />
            {/* Role-gated rail cards: merch seat gets the fan-signup QR; content
                seats get the "needs media" punch list. */}
            {isMerch && <MerchQrCard user={user} />}
            {isContent && <NeedsMediaCard />}
            {/* Merch revenue is a money surface, money group only. */}
            {canSeeMoney(user) && <Merch merch={merch} />}
            <Upcoming shows={upcomingShows} />
            {/* Ops roles relocate To-Dos out of the rail (band member → full-width
                below the grid; TM → under its strip in the main column). */}
            {/* audit 2026-07-02: pass reload so task edits refresh the list. */}
            {!trimmed && <Todo todos={todos} onChanged={reload} />}
          </aside>
        </div>

        {/* Band member: full-width To-Dos under Recent shows. */}
        {isBandMember && (
          <section className="dash-section">
            {/* audit 2026-07-02: pass reload so task edits refresh the list. */}
            <Todo todos={todos} onChanged={reload} />
          </section>
        )}

        {/* Top Cities / Audience hidden for the ops roles (band member + TM). */}
        {!trimmed && (
          <>
            {/* WHERE YOUR AUDIENCE LIVES, cross-platform audience by city */}
            <TopCities data={topCities} />

            {/* AUDIENCE, streaming + social, unified */}
            <section className="dash-section" id="audience-section">
              <header className="section-label">
                <h3 className="section-label__title">Audience</h3>
              </header>

              <div className="streaming-grid">
                <StreamingSection spotify={spotify} topPlaylists={topPlaylists} bandId={bandId} />
                <AudienceRail stats={stats?.raw ?? null} />
              </div>

              <div className="audience-foot">
                <Link className="btn-link" to="/content">
                  Full breakdown in Content <ArrowRight />
                </Link>
              </div>
            </section>
          </>
        )}

        <DashFooter />
      </main>
    </>
  );
}
